<template>
  <div>
    <div class="select-all-container" v-if="showSelectAll">
      <div class="select-all-checkbox">
        <input 
          type="checkbox"
          :checked="selectedLessons.size === filteredLessons.length"
          @change="toggleSelectAll"
          id="select-all"
          aria-label="Select all lessons"
        >
        <label for="select-all" class="select-all-label">Select All Lessons</label>
      </div>
      
      <div class="bulk-actions" v-if="selectedLessons.size > 0">
        <button @click="showBulkActionsMenu = !showBulkActionsMenu" class="bulk-actions-button">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bulk-icon">
            <circle cx="12" cy="12" r="1"/>
            <circle cx="12" cy="5" r="1"/>
            <circle cx="12" cy="19" r="1"/>
          </svg>
          Bulk Actions
        </button>
        
        <div class="bulk-actions-menu" v-if="showBulkActionsMenu">
          <div class="bulk-action-item" @click="$emit('bulk-move')">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bulk-action-icon">
              <path d="M9 18l6-6-6-6"/>
            </svg>
            Move to Course
          </div>
          <div class="bulk-action-item" @click="$emit('bulk-status')">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bulk-action-icon">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            Update Status
          </div>
          <div class="bulk-action-item" @click="$emit('bulk-export')">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bulk-action-icon">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export Selected
          </div>
          <div class="bulk-action-item delete" @click="$emit('bulk-delete')">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bulk-action-icon">
              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
            Delete Selected
          </div>
        </div>
      </div>
    </div>
    
    <div class="lessons-grid">
      <div 
        v-for="lesson in filteredLessons" 
        :key="lesson.id" 
        class="lesson-card"
      >
        <div class="lesson-card-header">
          <div class="lesson-thumbnail">
            <img 
              :src="lesson.lesson_temp_file || 'https://via.placeholder.com/300x200?text=No+Thumbnail'" 
              :alt="lesson.title"
              class="thumbnail-img"
            >
          </div>
          <span class="status-badge" :class="lesson.lesson_status">
            {{ lesson.lesson_status }}
          </span>
          
          <div class="progress-indicator" :class="getProgressClass(lesson)">
            <svg width="60" height="60" viewBox="0 0 60 60">
              <circle class="progress-bg" cx="30" cy="30" r="24" />
              <circle 
                class="progress-circle" 
                cx="30" 
                cy="30" 
                r="24" 
                :stroke-dasharray="`${getProgressPercentage(lesson) * 150.8} 150.8`" 
              />
              <text x="30" y="30" class="progress-text">{{ Math.round(getProgressPercentage(lesson) * 100) }}%</text>
            </svg>
          </div>
        </div>
        
        <div class="lesson-card-content">
          <h3 class="lesson-title">{{ lesson.title }}</h3>
          <p class="lesson-description">{{ lesson.description }}</p>
          
          <div class="lesson-meta">
            <div class="meta-item">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="meta-icon">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              <span>{{ formatDuration(lesson.duration) }}</span>
            </div>
            <div class="meta-item">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="meta-icon">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
              </svg>
              <span>{{ lesson.course?.title }}</span>
            </div>
          </div>
        </div>
        
        <div class="lesson-card-actions">
          <button @click="$emit('play-lesson', lesson)" class="card-action-button play" title="Play Lesson">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="action-icon">
              <polygon points="5 3 19 12 5 21 5 3"/>
            </svg>
          </button>
          <button @click="$emit('view-lesson', lesson)" class="card-action-button view" title="View Details">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="action-icon">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
          <button @click="$emit('edit-lesson', lesson)" class="card-action-button edit" title="Edit Lesson">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="action-icon">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
          <button @click="$emit('delete-lesson', lesson)" class="card-action-button delete" title="Delete Lesson">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="action-icon">
              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
          </button>
        </div>
        
        <div class="lesson-card-checkbox">
          <input 
            type="checkbox" 
            :checked="selectedLessons.has(lesson.id)"
            @change="$emit('toggle-selection', lesson.id)"
            :aria-label="'Select ' + lesson.title"
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LessonsGrid',
  props: {
    filteredLessons: {
      type: Array,
      required: true
    },
    selectedLessons: {
      type: Set,
      required: true
    },
    showSelectAll: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      showBulkActionsMenu: false
    }
  },
  methods: {
    getProgressPercentage(lesson) {
      if (lesson.completion_percentage) {
        return lesson.completion_percentage / 100;
      }
      
      if (lesson.lesson_status === 'completed') {
        return 1;
      } else if (lesson.lesson_status === 'in_progress') {
        return Math.max(0.1, Math.min(0.9, (lesson.id % 9) / 10 + 0.1));
      }
      return 0;
    },
    getProgressClass(lesson) {
      const percentage = this.getProgressPercentage(lesson);
      if (percentage >= 1) {
        return 'progress-completed';
      } else if (percentage > 0) {
        return 'progress-in-progress';
      }
      return 'progress-not-started';
    },
    toggleSelectAll() {
      this.$emit('toggle-select-all');
    },
    formatDuration(seconds) {
      if (!seconds && seconds !== 0) return '0 min';
      return `${Math.round(seconds / 60)} min`;
    }
  }
}
</script>

<style scoped>
.select-all-container {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.75rem 1.25rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  justify-content: space-between;
}

.select-all-checkbox {
  display: flex;
  align-items: center;
}

.select-all-container input[type="checkbox"] {
  margin-right: 0.5rem;
  width: 18px;
  height: 18px;
}

.select-all-label {
  font-size: 0.875rem;
  color: #475569;
  font-weight: 500;
}

.bulk-actions {
  position: relative;
}

.bulk-actions-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #f1f5f9;
  color: #475569;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.bulk-actions-button:hover {
  background: #e2e8f0;
  transform: translateY(-1px);
}

.bulk-icon {
  width: 16px;
  height: 16px;
}

.bulk-actions-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  z-index: 10;
}

.bulk-action-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.bulk-action-item:hover {
  background: #f8fafc;
}

.bulk-action-item.delete {
  color: #dc2626;
}

.bulk-action-icon {
  width: 18px;
  height: 18px;
}

.lessons-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.lesson-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  width: calc(33.33% - 1rem);
  position: relative;
  transition: all 0.3s ease;
  border: 1px solid #e2e8f0;
}

.lesson-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
  border-color: #cbd5e1;
}

.lesson-card-header {
  position: relative;
}

.lesson-thumbnail {
  width: 100%;
  height: 200px;
  overflow: hidden;
  border-radius: 12px 12px 0 0;
}

.thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.status-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.375rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
}

.status-badge.uploaded {
  background: #dcfce7;
  color: #166534;
}

.status-badge.pending {
  background: #fef3c7;
  color: #9a3412;
}

.status-badge.completed {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.in_progress {
  background: #dbeafe;
  color: #1e40af;
}

.status-badge.not_started {
  background: #f1f5f9;
  color: #475569;
}

.progress-indicator {
  position: absolute;
  bottom: -20px;
  right: 20px;
  z-index: 20;
  background: white;
  border-radius: 50%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-bg {
  fill: none;
  stroke: #e2e8f0;
  stroke-width: 4;
}

.progress-circle {
  fill: none;
  stroke-width: 4;
  stroke-linecap: round;
  transform: rotate(-90deg);
  transform-origin: center;
  transition: stroke-dasharray 0.5s ease;
}

.progress-text {
  text-anchor: middle;
  dominant-baseline: middle;
  font-size: 12px;
  font-weight: 700;
  fill: #475569;
}

.progress-completed .progress-circle {
  stroke: #10b981;
}

.progress-completed .progress-text {
  fill: #065f46;
}

.progress-in-progress .progress-circle {
  stroke: #3b82f6;
}

.progress-in-progress .progress-text {
  fill: #1e40af;
}

.progress-not-started .progress-circle {
  stroke: #94a3b8;
}

.progress-not-started .progress-text {
  fill: #475569;
}

.lesson-card-content {
  padding: 1.5rem;
}

.lesson-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 0.75rem;
}

.lesson-description {
  font-size: 0.9375rem;
  color: #475569;
  margin: 0 0 1.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.lesson-meta {
  display: flex;
  gap: 1rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.meta-icon {
  width: 18px;
  height: 18px;
}

.lesson-card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.card-action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.card-action-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(100%);
  transition: transform 0.2s ease;
}

.card-action-button:hover::before {
  transform: translateY(0);
}

.card-action-button.play {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
}

.card-action-button.play:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(37, 99, 235, 0.3);
}

.card-action-button.edit {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  box-shadow: 0 2px 4px rgba(5, 150, 105, 0.2);
}

.card-action-button.edit:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(5, 150, 105, 0.3);
}

.card-action-button.delete {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  box-shadow: 0 2px 4px rgba(220, 38, 38, 0.2);
}

.card-action-button.delete:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(220, 38, 38, 0.3);
}

.action-icon {
  width: 18px;
  height: 18px;
  stroke-width: 2;
  transition: transform 0.2s ease;
}

.card-action-button:hover .action-icon {
  transform: scale(1.1);
}

.lesson-card-checkbox {
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 10;
}

.lesson-card-checkbox input[type="checkbox"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

@media (max-width: 1200px) {
  .lesson-card {
    width: calc(50% - 0.75rem);
  }
}

@media (max-width: 768px) {
  .lesson-card {
    width: 100%;
  }
  
  .lesson-thumbnail {
    height: 180px;
  }
}
</style> 